"use client";

import { useState, useEffect, type ReactNode } from "react";
import Image from "next/image";
import { ArrowUpRight } from "@phosphor-icons/react";
import { useTranslations } from "next-intl";

export interface SheetMetalTrustedPartsProps {
	/** 轮播图片 URL 列表 */
	images: readonly string[];
	/** 「Learn More」外链 */
	learnMoreHref: string;
	/** 翻译命名空间 */
	translationNamespace?: string;
	/** 自动轮播间隔（毫秒），0 表示关闭 */
	autoplayMs?: number;
}

const GlobeIcon = ({ className }: { className?: string }) => (
	<svg
		className={`h-14 w-14 sm:h-16 sm:w-16 md:h-[72px] md:w-[72px] lg:h-20 lg:w-20 ${
			className ?? "text-[#0d68ea]"
		}`}
		viewBox="0 0 48 48"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<circle cx="24" cy="24" r="20" />
		<ellipse cx="24" cy="24" rx="20" ry="8" />
		<ellipse cx="24" cy="24" rx="8" ry="20" />
	</svg>
);

/** 绿色圆底白勾（与项目其他服务区块一致） */
function CheckItem({ children }: { children: ReactNode }) {
	return (
		<li className="flex items-start gap-2.5 text-sm text-black md:text-base">
			<span
				className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-themePrimary400 text-white"
				aria-hidden
			>
				<svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path
						d="M1 5L4.5 8.5L11 1.5"
						stroke="currentColor"
						strokeWidth="1.8"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</span>
			<span>{children}</span>
		</li>
	);
}

/**
 * Trusted Sheet Metal Parts：左栏单图轮播 + 右栏多段文案、要点、Learn More
 */
export default function SheetMetalTrustedParts({
	images,
	learnMoreHref,
	translationNamespace = "sheetMetalFabricationTrustedParts",
	autoplayMs = 5000,
}: SheetMetalTrustedPartsProps) {
	const t = useTranslations(translationNamespace);
	const [activeIndex, setActiveIndex] = useState(0);
	const count = images.length;

	useEffect(() => {
		if (count <= 1 || autoplayMs <= 0) return;
		const id = setInterval(() => {
			setActiveIndex((i) => (i + 1) % count);
		}, autoplayMs);
		return () => clearInterval(id);
	}, [count, autoplayMs]);

	if (count === 0) return null;

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 标题左、右上角绿色圆形图标 */}
				<div className="mb-8 flex items-start justify-between gap-4 md:mb-10 lg:mb-12">
					<h2 className="max-w-[min(100%,50rem)] text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<GlobeIcon className="shrink-0 text-themePrimary400" />
				</div>

				<div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,42%)_minmax(0,58%)] lg:gap-12 xl:gap-16">
					{/* 左：单图轮播 + 圆点 */}
					<div className="w-full lg:max-w-none">
						<div
							className="relative aspect-[5/5.2] w-full max-w-xl overflow-hidden rounded-sm bg-gray-100 lg:max-w-none"
							aria-roledescription="carousel"
						>
							{images.map((src, i) => (
								<div
									key={src}
									className={`absolute inset-0 transition-opacity duration-500 ease-out ${
										i === activeIndex ? "z-[1] opacity-100" : "z-0 opacity-0"
									}`}
									aria-hidden={i !== activeIndex}
								>
									<Image
										src={src}
										alt={t("imageAlt", { index: i + 1 })}
										fill
										className="object-cover"
										sizes="(max-width: 1024px) 100vw, 42vw"
										priority={i === 0}
									/>
								</div>
							))}
						</div>

						{count > 1 && (
							<div
								className="mt-4 flex justify-center gap-1"
								role="tablist"
								aria-label={t("carouselAriaLabel")}
							>
								{images.map((_, i) => (
									<button
										key={i}
										type="button"
										role="tab"
										aria-selected={activeIndex === i}
										aria-label={t("goToSlide", { slide: i + 1 })}
										onClick={() => setActiveIndex(i)}
										className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
									>
										<span
											className={`block h-1.5 w-1.5 rounded-full transition-colors ${
												activeIndex === i ? "bg-themePrimary400" : "bg-[#e8f5e0] hover:bg-[#c8e6c9]"
											}`}
										/>
									</button>
								))}
							</div>
						)}
					</div>

					{/* 右：正文、列表、外链 */}
					<div className="flex min-w-0 flex-col gap-5 text-left text-sm leading-relaxed text-black sm:gap-6 md:text-base">
						<p>{t("paragraph1")}</p>
						<p>{t("paragraph2")}</p>
						<p>{t("paragraph3")}</p>
						<p>{t("paragraph4")}</p>

						<ul className="mt-1 space-y-3 sm:space-y-3.5">
							<CheckItem>{t("bullet1")}</CheckItem>
							<CheckItem>{t("bullet2")}</CheckItem>
							<CheckItem>{t("bullet3")}</CheckItem>
							<CheckItem>{t("bullet4")}</CheckItem>
						</ul>

						<a
							href={learnMoreHref}
							target="_blank"
							rel="noopener noreferrer"
							className="mt-2 inline-flex items-center gap-1.5 font-semibold text-themePrimary400 transition-opacity hover:opacity-80"
						>
							{t("learnMore")}
							<ArrowUpRight className="h-4 w-4 sm:h-[18px] sm:w-[18px]" weight="bold" aria-hidden />
						</a>
					</div>
				</div>
			</div>
		</section>
	);
}
